import React, { useEffect, useRef, useState } from 'react';

interface ResultFieldProps {
  value: string;
  index: number;
  responses: string[];
  setResponses: React.Dispatch<React.SetStateAction<string[]>>;
}

const ResultField: React.FC<ResultFieldProps> = ({
                                                       value,
                                                       index,
                                                       responses,
                                                       setResponses,
                                                     }) => {
  const contentEditableRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Update the contenteditable div's innerHTML when the value prop changes
  useEffect(() => {
    if (contentEditableRef.current && !isFocused) {
      contentEditableRef.current.innerHTML = value;
    }
  }, [value, isFocused]);

  const handleEditResponse = (newValue: string) => {
    const updatedResponses = [...responses];
    updatedResponses[index] = newValue;
    setResponses(updatedResponses);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Check if Ctrl (or Command on Mac) + B is pressed
    if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
      e.preventDefault();
      document.execCommand('bold', false);
    }

    // Check if Ctrl (or Command on Mac) + I is pressed
    if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
      e.preventDefault();
      document.execCommand('italic', false);
    }
  };

  const handleInput = () => {
    if (contentEditableRef.current) {
      const newValue = contentEditableRef.current.innerHTML;
      handleEditResponse(newValue);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <div
      ref={contentEditableRef}
      contentEditable
      tabIndex={0} // Make the div focusable
      onKeyDown={handleKeyDown}
      onInput={handleInput}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={{
        marginTop: '2rem',
        padding: '8px',
        whiteSpace: 'pre-wrap', // Ensures new lines and spaces are preserved

        minHeight: '40px',
        outline: 'none',
        width: '100%', // Match fullWidth behavior
        fontFamily: 'inherit', // Match TextField font
        fontSize: 'inherit', // Match TextField font size
        lineHeight: 'inherit', // Match TextField line height
        backgroundColor: 'transparent', // Match TextField background
      }}
    />
  );
};

export default ResultField;
